/**
 * The settings of one question — a different panel per type.
 *
 * The numbers (`min`, `max`, `maxLength`, `minSelections`, `maxSelections`) are **one value for
 * the three languages** and are only editable on the base tab: a scale that ran 1–5 in Portuguese
 * and 1–7 in Spanish would come back as two incompatible distributions under one `key`, and
 * nobody would see it until the results did. The labels around them are per language, because
 * they are text people read.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTTextField } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { SurveyLanguage, SurveyQuestionType } from '../../../Types/Surveys.types';
import type { QuestionConfigNumbers, QuestionConfigTexts } from '../../../Utils/surveyForm';
import { BASE_LANGUAGE } from '../../../Utils/surveyForm';

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--space-s) var(--space-m);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const labelStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const hintStyle = css`
  grid-column: 1 / -1;
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const emptyStyle = css`
  margin: 0;
  padding: var(--space-s) var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

interface FieldProps {
  span: number;
  name: string;
  children: JSX.Element;
}

const Field = ({ span, name, children }: FieldProps): JSX.Element => (
  <div className={fieldStyle} style={{ gridColumn: `span ${span}` }}>
    <span className={labelStyle}>{name}</span>
    {children}
  </div>
);

export interface QuestionConfigPanelProps {
  id: string;
  type: SurveyQuestionType;
  numbers: QuestionConfigNumbers;
  texts: QuestionConfigTexts;
  language: SurveyLanguage;
  onNumberChange: (field: keyof QuestionConfigNumbers, value: string) => void;
  onTextChange: (field: keyof QuestionConfigTexts, value: string) => void;
}

export const QuestionConfigPanel = ({
  id,
  type,
  numbers,
  texts,
  language,
  onNumberChange,
  onTextChange,
}: QuestionConfigPanelProps): JSX.Element => {
  const isBase = language === BASE_LANGUAGE;
  const numberValue = (field: keyof QuestionConfigNumbers): string =>
    numbers[field] === undefined ? '' : String(numbers[field]);

  const numberField = (
    field: keyof QuestionConfigNumbers,
    span: number,
    placeholder?: string,
  ): JSX.Element => (
    <Field span={span} name={field}>
      <UTTextField
        id={`${id}-config-${field}`}
        type='number'
        value={numberValue(field)}
        placeholder={placeholder}
        disabled={!isBase}
        onChange={event => {
          onNumberChange(field, event.target.value);
        }}
      />
    </Field>
  );

  const textField = (field: keyof QuestionConfigTexts, span: number): JSX.Element => (
    <Field span={span} name={field}>
      <UTTextField
        id={`${id}-config-${field}`}
        value={texts[field]}
        onChange={event => {
          onTextChange(field, event.target.value);
        }}
      />
    </Field>
  );

  if (type === 'RATING') {
    return (
      <div className={gridStyle}>
        {numberField('min', 2)}
        {numberField('max', 2)}
        {textField('minLabel', 4)}
        {textField('maxLabel', 4)}
        <p className={hintStyle}>
          Os resultados trazem a escala inteira, inclusive as notas que ninguém deu — um gráfico que
          some com elas se lê como se a escala fosse menor do que é.
          {isBase ? '' : ' Os números só mudam na aba base (inglês).'}
        </p>
      </div>
    );
  }

  if (type === 'NPS') {
    return (
      <div className={gridStyle}>
        {textField('leftLabel', 6)}
        {textField('rightLabel', 6)}
        <p className={hintStyle}>
          A escala do NPS é fixa de 0 a 10. Só os rótulos das pontas são seus.
        </p>
      </div>
    );
  }

  if (type === 'BOOLEAN') {
    return (
      <div className={gridStyle}>
        {textField('trueLabel', 6)}
        {textField('falseLabel', 6)}
        <p className={hintStyle}>
          Numa grade, uma linha sim/não vem sem média: sim e não não têm ordem, e inventar uma
          imprimiria um número sem significado.
        </p>
      </div>
    );
  }

  if (type === 'SHORT_TEXT' || type === 'LONG_TEXT') {
    return (
      <div className={gridStyle}>
        {numberField('maxLength', 3)}
        {textField('placeholder', 9)}
        <p className={hintStyle}>
          Texto aberto não entra em média nenhuma: os resultados dizem quantas pessoas escreveram, e
          o conteúdo fica na aba de comentários.
        </p>
      </div>
    );
  }

  if (type === 'MULTI_CHOICE') {
    return (
      <div className={gridStyle}>
        {numberField('minSelections', 3, '1')}
        {numberField('maxSelections', 3, 'sem limite')}
        <p className={hintStyle}>
          O percentual de cada opção divide por quem respondeu a pergunta, não pelo total de
          marcações — então a soma passa de 100%, e é essa a frase certa.
          {isBase ? '' : ' Os números só mudam na aba base (inglês).'}
        </p>
      </div>
    );
  }

  if (type === 'MATRIX') {
    return (
      <p id={`${id}-config-empty`} className={emptyStyle}>
        A grade não tem configuração própria: a escala são as opções abaixo, e cada linha é
        respondida contra ela. A média de uma linha é a posição na escala — a ordem em que você
        escrever as opções é o que define essa ordem.
      </p>
    );
  }

  return (
    <p id={`${id}-config-empty`} className={emptyStyle}>
      Escolha única não tem configuração própria — só a lista de opções abaixo.
    </p>
  );
};
